import {
    useGetReportItemsQuery,
    useGetReportQuery,
    useGetReportTestSuitesQuery
} from '@app.raytd.com/store';
import { useMemo } from 'react';
import { ReportTestItem } from '../utils/entities';

import { skipToken } from '@reduxjs/toolkit/query';
import { TestSuite } from 'store/src/lib/tests/entity';
import getCustomFieldDisplayValue from '../utils/getCustomFieldDisplayValue';

// --- Hook Implementation ---

function useReportQueries(reportId: string) {

    const {
        data: rawTestItems,
        error: itemsError,
        isLoading: itemsLoading,
    } = useGetReportItemsQuery(reportId ? reportId : skipToken);

    const {
        data: report,
        error: reportError,
        isLoading: reportLoading,
    } = useGetReportQuery(reportId ? reportId : skipToken);

    const {
        data: testSuites,
        isLoading: isTestSuitesLoading,
        error: testSuitesError,
    } = useGetReportTestSuitesQuery(reportId ? parseInt(reportId, 10) : skipToken);

    // Memoize the enrichment process, explicitly typing the return value
    const enrichedTestItems: ReportTestItem[] = useMemo(() => {
        // Use StoreReportTestItem for input type check
        if (!rawTestItems || !testSuites || !Array.isArray(rawTestItems) || !Array.isArray(testSuites)) {
            return []; // Return empty array if data not ready
        }

        const testSuiteMap = new Map<number, TestSuite>(testSuites.map(ts => [ts.id, ts]));

        // Map from StoreReportTestItem to local ReportTestItem
        return rawTestItems.map((item): ReportTestItem => {
            // Construct the base object, assuming local ReportTestItem is a superset
            // This requires careful mapping or asserting types if structures differ significantly
            const enrichedItemBase = item as unknown as ReportTestItem; 
            const enrichedItem: ReportTestItem = {
                ...enrichedItemBase, // Spread the base item (potentially cast)
                customDisplayValues: {}, // Initialize the new property
            };

            // Use item.testSpecification.testSuiteId for lookup
            const testSuite = item.testSpecification?.testSuiteId 
                ? testSuiteMap.get(item.testSpecification.testSuiteId)
                : undefined;

            if (testSuite?.custom_fields && item.custom) {
                testSuite.custom_fields.forEach(fieldDefinition => {
                    if (fieldDefinition.active) {
                        const rawValue = item.custom[fieldDefinition.field_id];
                        const displayValue = getCustomFieldDisplayValue(fieldDefinition, rawValue);
                        enrichedItem.customDisplayValues[fieldDefinition.field_id] = displayValue;
                    }
                });
            }
            return enrichedItem;
        });
    }, [rawTestItems, testSuites]);

    // Combine loading and error states
    const isLoading = itemsLoading || reportLoading || isTestSuitesLoading;
    const error = itemsError || reportError || testSuitesError;

    return {
        allTestItems: enrichedTestItems, // Now correctly typed
        report,
        testSuites,
        isLoading,
        error,
    };
}

export default useReportQueries;